"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { BRAND } from "@/app/lib/content";
import { finaleHeadline, type IntentionId } from "./content";
import { trackLandingEvent } from "./analytics";

function LineIcon({ name }: { name: "arrow" | "check" | "shield" | "heart" | "people" | "lock" }) {
  const paths = {
    arrow: <><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    shield: <path d="M12 3 5 6v5c0 4.6 2.8 8.1 7 10 4.2-1.9 7-5.4 7-10V6l-7-3Z" />,
    heart: <path d="M20.8 5.7a5.5 5.5 0 0 0-7.8 0L12 6.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 22l8.8-8.5a5.5 5.5 0 0 0 0-7.8Z" />,
    people: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
    lock: <><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  };
  return <svg className="line-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

export function ConversationDemo() {
  return (
    <section id="conversation" className="landing-section conversation-section" aria-labelledby="conversation-title">
      <div className="scene-inner conversation-shell">
        <div className="chat-copy">
          <p className="landing-kicker">First conversation</p><h2 id="conversation-title">Start somewhere that matters.</h2>
          <p>Use something you already share to begin a more natural conversation. The suggestion is optional; your voice stays yours.</p>
          <p className="conversation-assurance">Suggestions use approved profile and compatibility information. They never send automatically.</p>
          <div className="conversation-signals"><span><LineIcon name="heart" /> Built from a shared value</span></div>
        </div>
      </div>
    </section>
  );
}

export function RealLifeJourney() {
  const steps = ["Discover", "Understand", "Match", "Talk", "Meet", "Build"];
  return (
    <section id="real-life" className="landing-section real-life-section" aria-labelledby="real-life-title">
      <div className="real-life-photo" aria-hidden="true"><picture><source media="(max-width: 640px)" srcSet="/images/landing/cafe-900.webp" /><Image src="/images/landing/cafe-1600.webp" alt="" fill sizes="100vw" /></picture></div>
      <div className="scene-inner">
        <p className="landing-kicker">From chat to real life</p><h2 id="real-life-title">Because the goal was never to stay on the app.</h2>
        <p className="real-life-lede">Meet with intention. Build something meaningful. A relationship that lasts may mean you no longer need Vowent.</p>
        <ol className="journey-line">{steps.map((step, index) => <li key={step}><span>0{index + 1}</span>{step}</li>)}</ol>
      </div>
    </section>
  );
}

export function WaysToMeet() {
  return <section id="ways-to-meet" className="landing-section ways-section" aria-labelledby="ways-title"><div className="scene-inner"><div className="section-heading centered"><p className="landing-kicker">Ways to meet</p><h2 id="ways-title">Choose the setting that feels right.</h2><p>Not every connection starts in the same way. These are two considered routes into real life.</p></div><div className="ways-grid"><article><div className="way-number">01</div><p className="way-kicker">For two people</p><h3>Date Nights</h3><p>Turn a mutual connection into a plan with a clear place, time and intention.</p><div className="way-meta"><span>One-to-one</span><span>Make a plan together</span></div></article><article><div className="way-number">02</div><p className="way-kicker">For a small group</p><h3>Hangouts</h3><p>A more relaxed way to meet through a shared plan before a one-to-one date.</p><div className="way-meta"><span>Small group</span><span>Public-first</span></div></article></div></div></section>;
}

export function ComparisonExperience() {
  const [vowent, setVowent] = useState(true);
  const rows = [
    ["Endless profiles", "A considered deck"], ["Surface attraction", "Deeper compatibility"],
    ["Unclear intentions", "Intentions made visible"], ["Generic matches", "Reasons you may align"],
    ["Awkward openings", "Meaningful conversation prompts"], ["Staying in-app", "Moving towards real relationships"],
  ];
  return (
    <section className={`landing-section comparison-section ${vowent ? "show-vowent" : "show-ordinary"}`} aria-labelledby="comparison-title">
      <div className="scene-inner">
        <div className="section-heading centered"><p className="landing-kicker">A different rhythm</p><h2 id="comparison-title">What changes when intention leads?</h2></div>
        <div className="comparison-toggle" role="group" aria-label="Compare product approaches"><button type="button" aria-pressed={!vowent} onClick={() => setVowent(false)}>Other dating apps</button><button type="button" aria-pressed={vowent} onClick={() => setVowent(true)}>Vowent</button></div>
        <div className="comparison-list">{rows.map(([ordinary, intentional], index) => <div key={ordinary}><span>0{index + 1}</span><p className="ordinary-copy">{ordinary}</p><i aria-hidden="true">→</i><p className="vowent-copy">{intentional}</p></div>)}</div>
      </div>
    </section>
  );
}

export function TrustAndVerification() {
  return (
    <section id="trust" className="landing-section trust-section" aria-labelledby="trust-title" onMouseEnter={() => trackLandingEvent("safety_section_viewed")}>
      <div className="scene-inner trust-layout"><div className="section-heading"><p className="landing-kicker">Trust, without false certainty</p><h2 id="trust-title">Safety tools. Clear expectations.</h2><p>Verification adds context. It does not replace your judgement, guarantee identity or promise that another person is safe.</p>
        <nav className="trust-links" aria-label="Trust and legal information"><Link href="/child-safety">Child safety standards</Link><Link href="/safety">Safety approach</Link><Link href="/privacy">Privacy notice</Link><Link href="/contact">Support</Link></nav></div>
        <div className="trust-product" aria-label="Vowent safety status example">
          <div className="trust-product-head"><span className="trust-emblem"><LineIcon name="shield" /></span><div><p>Privacy &amp; safety</p><h3>More control, from the start.</h3></div></div>
          <dl className="trust-facts">
            <div><LineIcon name="check" /><dt>Account confirmed</dt><dd>Phone and email controls help protect access to the account.</dd></div>
            <div><LineIcon name="lock" /><dt>Privacy choices</dt><dd>Choose what you share and manage your information.</dd></div>
            <div><LineIcon name="shield" /><dt>Block &amp; report</dt><dd>Available from profiles and conversations when something feels wrong.</dd></div>
          </dl>
        </div>
      </div>
    </section>
  );
}

export function DownloadFinale({ intention }: { intention: IntentionId | null }) {
  return (
    <section id="download" className="landing-section download-finale" aria-labelledby="download-title" onMouseEnter={() => trackLandingEvent("availability_viewed")}>
      <div className="scene-inner finale-card">
        <Image className="finale-mark" src="/brand/vowent-mark-color.png" width={74} height={74} alt="" />
        <p className="landing-kicker">The paths meet</p>
        <h2 id="download-title">{finaleHeadline(intention)}</h2>
        <p className="finale-line">{BRAND.description}</p>
        <a className="download-button" href="/download" onClick={() => trackLandingEvent("availability_selected")}>Download Vowent<LineIcon name="arrow" /></a>
        <div className="finale-reassurance"><span><LineIcon name="people" /> Adults 21+</span><span>Built for meaningful relationships</span><span><LineIcon name="shield" /> Report and block controls</span></div>
      </div>
    </section>
  );
}
